/**
 * Copyright 2025 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

import {Component, createRef} from 'preact';
import PropTypes from 'prop-types';
import {Publishers, PublisherState} from '@phenixrts/sdk/publish';
import userDataStore from '../../shared/userDataStore';
import UserMediaPublisher from '../components/UserMediaPublisher';
import MediaUriPublisher from '../components/MediaUriPublisher';
import FullScreenMessage from '../../shared/components/FullScreenMessage';
import chromeSrc from '../../../../assets/chrome.png';
import firefoxSrc from '../../../../assets/firefox.png';
import appleSrc from '../../../../assets/apple.png';
import androidSrc from '../../../../assets/android.png';
import '../../../css/app.css';

const capabilityAudioOnly = 'audio-only';
const capabilityVideoOnly = 'video-only';
const capabilityPublishUri = 'publish-uri';
// API functions
const publishUri = async(uri, publishToken, publicationType, baseUrl, applicationId) => {
  const response = await fetch(`${baseUrl}/pcast/${applicationId}/stream/publish/uri/${publicationType}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${publishToken}`
    },
    body: JSON.stringify({uri})
  });

  return response.json();
};

const stopPublishingWithUri = async(baseUrl, applicationId, publishToken, streamId, sharedSecret) => {
  const response = await fetch(`${baseUrl}/pcast/${applicationId}/stream/${encodeURIComponent(streamId)}/destroy`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${publishToken}`
    },
    body: JSON.stringify({
      apiVersion: 7,
      sharedSecret,
      reason: 'web-channel-publisher:destroy'
    })
  });

  return response.json();
};

// Utility functions
const getPublicationType = (url) => {
  const {pathname, protocol} = new URL(url);
  const fileBasedProtocols = ['http:', 'https:', 'ftp:', 'ftps:', 'file:'];

  if (!fileBasedProtocols.includes(protocol)) {
    return protocol.replace(':', '');
  }

  const filename = pathname.split('/').pop();
  const fileExtensionStartIndex = filename.lastIndexOf('.');

  return fileExtensionStartIndex === -1 ? undefined : filename.slice(fileExtensionStartIndex + 1);
};

const getUnsupportedDeviceMessage = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isAndroid = ua.includes('android');
  const isIphone = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const unsupportedDeviceMessages = [
    'Your browser does not support WebRTC.',
    `Please try <img src=${chromeSrc} width="40"> Chrome or <img src=${firefoxSrc} width="40"> Firefox on a desktop.`
  ];

  if (isAndroid) {
    unsupportedDeviceMessages.push(`or try our <img src=${androidSrc} width="40"> Android App`);
  } else if (isIphone) {
    unsupportedDeviceMessages.push(`or try our <img src=${appleSrc} width="40"> iOS App`);
  }

  return unsupportedDeviceMessages;
};

class Publisher extends Component {
  constructor(props) {
    super(props);
    this._publisher = createRef();
    this._observers = [];

    const {storeLocalStateKey} = props.publisher;

    this.state = {
      errorMessage: null,
      isBusy: false,
      stats: [],
      streamUri: '',
      userMedia: null,
      publishWithUriResponse: null,
      publishedUri: '',
      // Quality settings
      gumResolution: Number(userDataStore.get(`${storeLocalStateKey}/channelPublisher/GumResolution`)) || null,
      aspectRatio: userDataStore.get(`${storeLocalStateKey}/channelPublisher/AspectRatio`) || '16x9',
      uriQuality: userDataStore.get(`${storeLocalStateKey}/channelPublisher/UriQuality`) || 'fhd',
      frameRate: parseInt(userDataStore.get(`${storeLocalStateKey}/channelPublisher/FrameRate`), 10) || 30
    };
  }

  componentDidMount() {
    // Test media permissions
    navigator.mediaDevices
      .getUserMedia({
        audio: true,
        video: true
      })
      .then(stream => {
        stream.getTracks().forEach(track => track.stop());
      })
      .catch(error => {
        console.error('Failed to get user media stream. Please grant permission for Camera and Microphone.', error);
        this._setErrorMessage(`Failed to get user media: ${error.message || error.name || error}`);
      });
  }

  componentWillUnmount() {
    while (this._observers.length > 0) {
      this._observers.pop().dispose();
    }

    this.stopPublisher();
  }

  // Error handling
  _setErrorMessage = (message) => {
    this.setState({
      errorMessage: typeof message === 'string' ? message : message.message,
      isBusy: false
    });
  };

  // State management helpers
  _updateQualitySetting = (key, value) => {
    userDataStore.put(`${this.props.publisher.storeLocalStateKey}/channelPublisher/${key}`, value);
    this.setState({
      [key.charAt(0).toLowerCase() + key.slice(1)]: value,
      errorMessage: null,
      isBusy: false
    });
  };

  // User media publishing
  _publishToChannel = async(userMedia) => {
    this.setState({isBusy: true});

    try {
      const publisher = Publishers.createPublisher({
        mediaStream: userMedia,
        name: `WebChannelPublisher-${new Date().toISOString().replace(/[^0-9|^T]/g, '').slice(0, -3)}`,
        token: this.props.publisher.token
      });
      const publisherStateObserver = (state) => {
        switch (state) {
          case PublisherState.Initializing:
            // Note: Application should never observe this state.
            break;

          case PublisherState.Starting:
            break;

          case PublisherState.Publishing:
            this._publisher.current = publisher;
            this.setState({
              userMedia,
              errorMessage: null,
              isBusy: false
            });

            break;

          case PublisherState.Stopped:
          case PublisherState.Offline:
            this._publisher.current = null;
            this.setState({
              userMedia: null,
              errorMessage: null,
              isBusy: false
            });

            break;

          case PublisherState.Unauthorized:
            this._setErrorMessage('Unauthorized. Verify publish token');

            break;

          case PublisherState.GeoRestricted:
            this._setErrorMessage('Unauthorized: GeoRestricted');

            break;

          case PublisherState.GeoBlocked:
            this._setErrorMessage('Unauthorized: GeoBlocked');

            break;

          case PublisherState.UnsupportedFeature:
            this._setErrorMessage('Error:Unsupported Feature');

            break;

          case PublisherState.NotFound:
            this._setErrorMessage('Error: Not Found');

            break;

          case PublisherState.ConfigurationError:
            this._setErrorMessage('Error: Publisher Configuration Error');

            break;

          case PublisherState.Recovering:
            break;

          case PublisherState.Reconnecting:
            break;

          default:
            console.error('Unknown publisher state');
        }
      };

      this._observers.push(
        publisher.state.subscribe(publisherStateObserver),
        publisher.rtcStatistics.subscribe(stats => this.setState({stats}))
      );
    } catch (error) {
      this._setErrorMessage(`Unable to publish to channel: ${error.message}`);
    }
  };

  stopPublisher = () => {
    this._publisher.current?.dispose();
  };

  publishUri = async(uri, publishToken, publicationType, baseUrl, applicationId) => {
    this.setState({isBusy: true});

    try {
      const responseBody = await publishUri(uri, publishToken, publicationType, baseUrl, applicationId);
      this.setState({
        publishWithUriResponse: responseBody,
        publishedUri: uri,
        errorMessage: null,
        isBusy: false
      });
    } catch (error) {
      this._setErrorMessage(`Unable to publish URI: ${error.message}`);
    }
  };

  _stopPublishingWithUri = async(baseUrl, applicationId, publishToken, streamId, sharedSecret) => {
    try {
      const response = await stopPublishingWithUri(baseUrl, applicationId, publishToken, streamId, sharedSecret);

      if (response.status !== 'ok') {
        throw new Error(typeof response === 'string' ? response : JSON.stringify(response));
      }

      this.setState({
        publishWithUriResponse: null,
        publishedUri: '',
        errorMessage: null,
        isBusy: false
      });
    } catch (error) {
      console.error('Error in _stopPublishingWithUri:', error);
      this._setErrorMessage(`Unable to stop publishing: ${error.message}`);
    }
  };

  _gotUserMedia = (error, userMedia) => {
    if (error) {
      this._setErrorMessage(`Failed to get user media: ${error.message || error.name || error}`);

      return;
    }

    if (userMedia) {
      this.setState({
        userMedia,
        errorMessage: null,
        isBusy: false
      });
    }
  };

  _changeGumResolution = (resolution) => this._updateQualitySetting('GumResolution', resolution);
  _changeAspectRatio = (aspectRatio) => this._updateQualitySetting('AspectRatio', aspectRatio);
  _changeUriQuality = (quality) => this._updateQualitySetting('UriQuality', quality);
  _changeFrameRate = (frameRate, frameRateMax) => {
    const finalFrameRate = frameRateMax || frameRate;
    this._updateQualitySetting('FrameRate', parseInt(finalFrameRate, 10));
  };
  _changeStreamUri = (streamUri) => this.setState({streamUri});

  // URI publishing helpers
  _publishToChannelWithMediaURI = (uri) => {
    try {
      const {token, decodedToken} = this.props.publisher;

      if (!token) {
        this._setErrorMessage('No publish token provided');

        return;
      }

      if (!decodedToken) {
        this._setErrorMessage('Invalid token');

        return;
      }

      if (!decodedToken.applicationId) {
        this._setErrorMessage('Token must contain applicationId');

        return;
      }

      const baseUrl = decodedToken.uri || `https://pcast.phenixrts.com`; // A token without a uri is a production token
      const publicationType = getPublicationType(uri);

      if (!publicationType) {
        this._setErrorMessage('Invalid input URI');

        return;
      }

      this.publishUri(uri, token, publicationType, baseUrl, decodedToken.applicationId);
    } catch (e) {
      this._setErrorMessage(e);
    }
  };

  _stopMediaURIPublisher = () => {
    try {
      const {token, decodedToken} = this.props.publisher;
      const {publishWithUriResponse} = this.state;

      if (!publishWithUriResponse) {
        this._setErrorMessage('No active URI publishing session to stop');

        return;
      }

      if (!token) {
        this._setErrorMessage('No publish token provided');

        return;
      }

      if (!decodedToken) {
        this._setErrorMessage('Invalid token');

        return;
      }

      const baseUrl = decodedToken.uri;
      const {sharedSecret, streamId} = publishWithUriResponse;

      if (!sharedSecret || !streamId) {
        this._setErrorMessage('Missing shared secret or stream ID from response');

        return;
      }

      this._stopPublishingWithUri(baseUrl, decodedToken.applicationId, token, streamId, sharedSecret);
    } catch (e) {
      console.error('Error stopping media URI publisher:', e);
      this._setErrorMessage(e);
    }
  };

  render() {
    const {
      errorMessage,
      isBusy,
      stats,
      streamUri,
      publishWithUriResponse,
      gumResolution,
      aspectRatio,
      frameRate,
      uriQuality
    } = this.state;
    const isPublishUriMode = this.props.publisher.capabilities.includes(capabilityPublishUri);

    if (!isPublishUriMode && typeof RTCPeerConnection === 'undefined') {
      return <FullScreenMessage messages={getUnsupportedDeviceMessage()}/>;
    }

    const publisher = this._publisher.current;
    const isVideoOnly = this.props.publisher.capabilities.includes(capabilityVideoOnly);
    const isAudioOnly = this.props.publisher.capabilities.includes(capabilityAudioOnly);

    return isPublishUriMode ? (
      <MediaUriPublisher
        publishToChannel={this._publishToChannelWithMediaURI}
        stopPublisher={this._stopMediaURIPublisher}
        errorMessage={errorMessage}
        changeUriQuality={this._changeUriQuality}
        changeStreamUri={(streamUri) => this.setState({streamUri})}
        uriQuality={uriQuality}
        streamUri={streamUri}
        publishWithUriResponse={publishWithUriResponse}
      />
    ) : (
      <UserMediaPublisher
        videoOnly={isVideoOnly}
        audioOnly={isAudioOnly}
        publishToChannel={this._publishToChannel}
        stopPublisher={this.stopPublisher}
        publisher={publisher}
        errorMessage={errorMessage}
        gotUserMedia={(error, {userMedia}) => this._gotUserMedia(error, userMedia)}
        changeGumResolution={this._changeGumResolution}
        changeAspectRatio={this._changeAspectRatio}
        changeFrameRate={this._changeFrameRate}
        gumResolution={gumResolution}
        aspectRatio={aspectRatio}
        frameRate={frameRate}
        isBusy={isBusy}
        stats={stats}
      />
    );
  }
}

Publisher.propTypes = {
  publisher: PropTypes.shape({
    storeLocalStateKey: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    decodedToken: PropTypes.shape({
      applicationId: PropTypes.string.isRequired,
      uri: PropTypes.string.isRequired
    }).isRequired,
    capabilities: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired
};

export default Publisher;